import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Radio, Play, Pause, Loader2 } from "lucide-react";
import radioConfig from "../config/radioConfig";
import { useAudioPlayer } from "../context/AudioPlayerContext";

const NAV = [
  { label: "Inicio", href: "#inicio", id: "inicio" },
  { label: "En vivo", href: "#en-vivo", id: "en-vivo" },
  { label: "Programación", href: "#programacion", id: "programacion" },
  { label: "La radio", href: "#la-radio", id: "la-radio" },
  { label: "Las Breñas Digital", href: "#partner", id: "partner" },
  { label: "Contacto", href: "#contacto", id: "contacto" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isPlaying, isLoading, toggle } = useAudioPlayer();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled
          ? "bg-[#070A0F]/85 backdrop-blur-xl border-b border-slate-800/80"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between h-16 sm:h-20">
        <a
          href="#inicio"
          data-testid="header-logo"
          className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 rounded-lg"
          aria-label="FM REAL 96.3 - Inicio"
        >
          <img
            src={radioConfig.assets.logo}
            alt="Logo FM REAL 96.3 - Las Breñas"
            className="h-10 w-10 sm:h-11 sm:w-11 rounded-lg object-contain ring-1 ring-cyan-500/30 transition-transform duration-300 group-hover:scale-105"
          />
          <div className="leading-none hidden xs:block">
            <span className="block font-heading font-extrabold text-white text-base sm:text-lg tracking-tight">
              FM REAL <span className="text-cyan-400">96.3</span>
            </span>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono mt-0.5">
              Las Breñas
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <a
              key={item.id}
              href={item.href}
              data-testid={`header-nav-link-${item.id}`}
              className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-cyan-400 transition-colors duration-200 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="header-listen-live-button"
            aria-label={isPlaying ? "Pausar transmisión en vivo" : "Escuchar en vivo"}
            className="hidden sm:inline-flex items-center gap-2 bg-cyan-400 hover:bg-cyan-300 text-[#070A0F] font-bold text-sm px-4 py-2.5 rounded-full transition-[background-color,transform] duration-200 hover:scale-[1.03] shadow-[0_0_24px_rgba(0,229,255,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {isLoading ? (
              <Loader2 size={15} className="animate-spin" />
            ) : isPlaying ? (
              <Pause size={14} fill="currentColor" />
            ) : (
              <Play size={14} fill="currentColor" />
            )}
            {isLoading ? "CONECTANDO…" : isPlaying ? "SONANDO EN VIVO" : "ESCUCHÁ EN VIVO"}
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="header-mobile-menu-toggle"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-[#070A0F]/97 backdrop-blur-xl border-b border-slate-800"
          >
            <nav className="px-4 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${item.id}`}
                  className="flex items-center gap-3 px-4 py-3.5 text-base font-medium text-slate-200 hover:text-cyan-400 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <Radio size={16} className="text-cyan-500/60" />
                  {item.label}
                </a>
              ))}
              <button
                onClick={() => {
                  toggle();
                  setOpen(false);
                }}
                data-testid="mobile-menu-listen-live-button"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-cyan-400 text-[#070A0F] font-bold px-4 py-3.5 rounded-full"
              >
                {isLoading ? "CONECTANDO…" : isPlaying ? "⏸ SONANDO EN VIVO" : "▶ ESCUCHÁ EN VIVO"}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
